import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown, Loader2, Check } from "lucide-react";

/**
 * QuestionFeedback — inline thumbs up/down (+ optional one-line "why" on
 * thumbs-down) shown under the explanation of any AI-generated question.
 *
 * Props:
 *   questionId (string, required)
 *   aiGenerated (bool, optional) — if false, render nothing.
 */
export default function QuestionFeedback({ questionId, aiGenerated = true }) {
  const [myRating, setMyRating] = useState(null);
  const [myReason, setMyReason] = useState("");
  const [aggregates, setAggregates] = useState({ up: 0, down: 0 });
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reasonDraft, setReasonDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!questionId || !aiGenerated) return;
    api
      .get(`/questions/${questionId}/feedback`)
      .then((r) => {
        setMyRating(r.data.my_rating);
        setMyReason(r.data.my_reason || "");
        setAggregates({ up: r.data.up, down: r.data.down });
      })
      .catch(() => {});
  }, [questionId, aiGenerated]);

  if (!aiGenerated || !questionId) return null;

  const submit = async (rating, reason = null) => {
    if (saving) return;
    setSaving(true);
    try {
      await api.post(`/questions/${questionId}/feedback`, { rating, reason });
      // Update aggregates optimistically
      const prev = myRating;
      setAggregates((a) => {
        const next = { ...a };
        if (prev === "up") next.up = Math.max(0, next.up - 1);
        if (prev === "down") next.down = Math.max(0, next.down - 1);
        if (rating === "up") next.up += 1;
        if (rating === "down") next.down += 1;
        return next;
      });
      setMyRating(rating);
      if (reason !== null) setMyReason(reason);
      toast.success(rating === "up" ? "Thanks — we'll keep questions like this." : "Got it — we'll steer away from this style.");
      if (rating === "down" && reason === null) {
        setReasonOpen(true);
      } else {
        setReasonOpen(false);
        setReasonDraft("");
      }
    } catch (e) {
      toast.error("Couldn't save feedback.");
    } finally {
      setSaving(false);
    }
  };

  const saveReason = async () => {
    const r = reasonDraft.trim();
    await submit("down", r);
  };

  return (
    <div
      className="mt-4 pt-3 flex items-start gap-3 text-xs flex-wrap"
      style={{ borderTop: "1px dashed var(--border)" }}
      data-testid={`qfb-root-${questionId}`}
    >
      <span style={{ color: "var(--text-muted)" }} className="pt-1.5">Was this a good question?</span>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => submit("up")}
          disabled={saving}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border transition-colors"
          style={{
            borderColor: myRating === "up" ? "var(--mastery)" : "var(--border)",
            background: myRating === "up" ? "var(--hl-blue)" : "transparent",
            color: myRating === "up" ? "var(--mastery)" : "var(--text-secondary)",
          }}
          data-testid={`qfb-up-${questionId}`}
          aria-label="Thumbs up"
        >
          {myRating === "up" ? <Check size={12} /> : <ThumbsUp size={12} />}
          {aggregates.up > 0 && <span>{aggregates.up}</span>}
        </button>
        <button
          onClick={() => submit("down")}
          disabled={saving}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border transition-colors"
          style={{
            borderColor: myRating === "down" ? "var(--weakness)" : "var(--border)",
            background: myRating === "down" ? "#fdecea" : "transparent",
            color: myRating === "down" ? "var(--weakness)" : "var(--text-secondary)",
          }}
          data-testid={`qfb-down-${questionId}`}
          aria-label="Thumbs down"
        >
          {myRating === "down" ? <Check size={12} /> : <ThumbsDown size={12} />}
          {aggregates.down > 0 && <span>{aggregates.down}</span>}
        </button>
      </div>

      {myRating === "down" && !reasonOpen && myReason && (
        <div className="w-full mt-1 pt-1 flex items-center gap-2 flex-wrap" data-testid={`qfb-shown-reason-${questionId}`}>
          <span style={{ color: "var(--text-muted)" }}>Your note:</span>
          <span className="italic" style={{ color: "var(--text-secondary)" }}>"{myReason}"</span>
          <button
            onClick={() => { setReasonDraft(myReason); setReasonOpen(true); }}
            className="underline"
            style={{ color: "var(--primary)" }}
            data-testid={`qfb-edit-reason-${questionId}`}
          >
            edit
          </button>
        </div>
      )}

      {reasonOpen && (
        <div className="w-full mt-1 flex items-center gap-2 flex-wrap" data-testid={`qfb-reason-form-${questionId}`}>
          <input
            type="text"
            value={reasonDraft}
            onChange={(e) => setReasonDraft(e.target.value.slice(0, 200))}
            placeholder="One line — what went wrong? (optional)"
            className="flex-1 min-w-[220px] px-2 py-1.5 rounded-lg border text-xs"
            style={{ borderColor: "var(--border)", background: "var(--paper)", color: "var(--text)" }}
            data-testid={`qfb-reason-input-${questionId}`}
            autoFocus
            maxLength={200}
          />
          <button
            onClick={saveReason}
            disabled={saving}
            className="pm-btn-primary text-xs py-1 px-3"
            data-testid={`qfb-reason-save-${questionId}`}
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : "Save"}
          </button>
          <button
            onClick={() => { setReasonOpen(false); setReasonDraft(""); }}
            className="text-xs"
            style={{ color: "var(--text-muted)" }}
            data-testid={`qfb-reason-cancel-${questionId}`}
          >
            skip
          </button>
        </div>
      )}
    </div>
  );
}
